import {Injectable} from '@angular/core';
import {delay, Observable, of} from 'rxjs';
import type {Contact, ContactEvent, ContactsListResult, EventMembersListQuery} from '../types/contacts.types';
import type {EventMembersApi} from './event-members-api.port';

const MOCK_DELAY_MS = 200;

function createMockContact(
  id: string,
  firstName: string,
  lastName: string,
  mobile: string,
  events: string[],
  memberEventDate: string | null = null,
): Contact {
  return {
    id,
    firstName,
    lastName,
    mobile,
    groups: ['مشتریان VIP'],
    events,
    eventIds: [],
    memberEventDate,
    createdAt: '1403/08/12',
  };
}

const SEED_CONTACTS: Contact[] = [
  createMockContact('101', 'سارا', 'محمدی', '09123456780', ['رویداد اول']),
  createMockContact('102', 'فاطمه', 'یوسفی', '09121234567', ['رویداد اول']),
  createMockContact('103', 'علی', 'رضایی', '09129876543', ['رویداد اول']),
  createMockContact('104', 'مریم', 'احمدی', '09127654321', ['رویداد اول']),
  createMockContact('105', 'حسین', 'کریمی', '09125551234', ['رویداد اول']),
  createMockContact('106', 'زهرا', 'نوری', '09123334455', ['یلدا']),
  createMockContact('107', 'رضا', 'موسوی', '09126667788', ['یلدا']),
  createMockContact('108', 'نرگس', 'جعفری', '09129990011', ['نوروز']),
  createMockContact('109', 'امیر', 'حسینی', '09124443322', ['نوروز']),
  createMockContact('110', 'لیلا', 'صادقی', '09127778899', ['تولدهای اردیبهشت'], null),
  createMockContact('111', 'ندا', 'کاظمی', '09121112233', ['تولدهای اردیبهشت'], '1403/02/12'),
  createMockContact('112', 'پویا', 'مرادی', '09123334455', ['تولدهای اردیبهشت'], '1403/02/25'),
  createMockContact('113', 'مینا', 'اکبری', '09125556677', ['تولدهای اردیبهشت'], null),
  createMockContact('114', 'سامان', 'نوری', '09128889900', ['رویداد تستی'], '1403/03/01'),
  createMockContact('115', 'هستی', 'رحیمی', '09120001122', ['رویداد تستی'], null),
];

const SEED_EVENT_NAMES = new Set([
  'رویداد اول',
  'یلدا',
  'نوروز',
  'تولدهای اردیبهشت',
  'رویداد تستی',
]);

@Injectable()
export class EventMembersApiMockAdapter implements EventMembersApi {
  private readonly membersByEventId = new Map<string, Contact[]>();
  private seededEventIds = new Set<string>();
  private lastContactId = 5000;

  seedMembersForEvents(events: ContactEvent[]): void {
    for (const event of events) {
      const alreadySeeded = this.seededEventIds.has(event.id);
      const currentMembers = this.membersByEventId.get(event.id) ?? [];
      const shouldReseed =
        SEED_EVENT_NAMES.has(event.name) && (!alreadySeeded || currentMembers.length === 0);

      if (!shouldReseed) {
        if (!alreadySeeded) {
          this.membersByEventId.set(event.id, []);
          this.seededEventIds.add(event.id);
        }
        continue;
      }

      const contacts = SEED_CONTACTS.filter(c => c.events.includes(event.name)).map(contact => ({
        ...contact,
        events: [event.name],
      }));
      this.membersByEventId.set(event.id, contacts);
      this.seededEventIds.add(event.id);
    }
  }

  listContactsInEvent(query: EventMembersListQuery): Observable<ContactsListResult> {
    const page = query.page ?? 1;
    const pageSize = query.pageSize ?? 10;
    const phrase = query.phrase?.trim().toLowerCase() ?? '';

    let items = [...(this.membersByEventId.get(query.eventId) ?? [])];

    if (phrase) {
      items = items.filter(contact => {
        const fullName = `${contact.firstName} ${contact.lastName}`.toLowerCase();
        return fullName.includes(phrase) || contact.mobile.includes(phrase);
      });
    }

    const total = items.length;
    const start = (page - 1) * pageSize;
    const paged = items.slice(start, start + pageSize);

    return of({items: paged, total}).pipe(delay(MOCK_DELAY_MS));
  }

  addMembersManual(
    eventId: string,
    items: {mobile: string; raiseDateTime?: number | null}[],
  ): Observable<void> {
    const members = [...(this.membersByEventId.get(eventId) ?? [])];
    for (const item of items) {
      const mobile = String(item.mobile ?? '').trim();
      if (!mobile) continue;
      const exists = members.some(m => m.mobile === mobile);
      if (exists) continue;

      this.lastContactId += 1;
      members.push(
        createMockContact(
          String(this.lastContactId),
          'مخاطب',
          String(this.lastContactId),
          mobile,
          ['رویداد'],
        ),
      );
    }
    this.membersByEventId.set(eventId, members);
    return of(undefined).pipe(delay(MOCK_DELAY_MS));
  }

  addMembersByFile(_eventId: string, _file: File): Observable<void> {
    // In mock mode we don't parse the file; treat as success.
    return of(undefined).pipe(delay(MOCK_DELAY_MS));
  }

  addMembersByContactIds(
    eventId: string,
    contactIds: string[],
    _raiseDateTime?: number | null,
  ): Observable<void> {
    const members = [...(this.membersByEventId.get(eventId) ?? [])];
    for (const id of contactIds) {
      const exists = members.some(m => m.id === id);
      if (exists) continue;

      this.lastContactId += 1;
      members.push(
        createMockContact(
          String(this.lastContactId),
          'مخاطب',
          String(this.lastContactId),
          `09${String(100000000 + (this.lastContactId % 900000000)).slice(-9)}`,
          ['رویداد'],
        ),
      );
    }
    this.membersByEventId.set(eventId, members);
    return of(undefined).pipe(delay(MOCK_DELAY_MS));
  }

  updateMemberEventDate(eventId: string, contactId: string, eventDate: string): Observable<void> {
    const members = [...(this.membersByEventId.get(eventId) ?? [])];
    const index = members.findIndex(contact => contact.id === contactId);
    if (index >= 0) {
      members[index] = {...members[index], memberEventDate: eventDate};
      this.membersByEventId.set(eventId, members);
    }
    return of(undefined).pipe(delay(MOCK_DELAY_MS));
  }

  getMemberCount(eventId: string): number {
    return this.membersByEventId.get(eventId)?.length ?? 0;
  }

  removeContactFromEvent(
    eventId: string,
    contactId: string,
    deleteGlobally: boolean,
  ): Observable<void> {
    const members = this.membersByEventId.get(eventId) ?? [];
    this.membersByEventId.set(
      eventId,
      members.filter(contact => contact.id !== contactId),
    );

    if (deleteGlobally) {
      for (const [id, contacts] of this.membersByEventId.entries()) {
        this.membersByEventId.set(
          id,
          contacts.filter(contact => contact.id !== contactId),
        );
      }
    }

    return of(undefined).pipe(delay(MOCK_DELAY_MS));
  }
}
